import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Dayjs } from 'dayjs';

interface HuntingLicensesFilterState {
  searchText: string;
  selectedLicenseTypeIds: number[];
  createdAtFromFilter: Dayjs | null | undefined;
  createdAtToFilter: Dayjs | null | undefined;
  fromFilter: Dayjs | null | undefined;
  toFilter: Dayjs | null | undefined;
  selectedLicenseTypeIdsTemporal: number[];
  showNotPrinted: boolean;
  notFormalVerified: boolean;
  selectedStatuses: number[];
  selectedStatusesTemporal: number[];
  selectedSalesPointIds: string[];
  selectedSalesPointIdsTemp: string[];
  mobileUsed: boolean | null;
  mobileUsedTemp: boolean | null;
}

const initialState: HuntingLicensesFilterState = {
  searchText: '',
  selectedLicenseTypeIds: [],
  createdAtFromFilter: undefined,
  createdAtToFilter: undefined,
  fromFilter: undefined,
  toFilter: undefined,
  selectedLicenseTypeIdsTemporal: [],
  showNotPrinted: false,
  notFormalVerified: false,
  selectedStatuses: [] as number[],
  selectedStatusesTemporal: [] as number[],
  selectedSalesPointIds: [],
  selectedSalesPointIdsTemp: [],
  mobileUsed: null,
  mobileUsedTemp: null,
};

const huntingLicensesFiltersSlice = createSlice({
  name: 'huntingLicensesFilters',
  initialState,
  reducers: {
    setSearchText(state, action: PayloadAction<string>) {
      state.searchText = action.payload;
    },
    setSelectedLicenseTypeIds(state, action: PayloadAction<number[]>) {
      state.selectedLicenseTypeIds = action.payload;
    },
    setCreatedAtFromFilter(state, action: PayloadAction<Dayjs | null | undefined>) {
      state.createdAtFromFilter = action.payload;
    },
    setCreatedAtToFilter(state, action: PayloadAction<Dayjs | null | undefined>) {
      state.createdAtToFilter = action.payload;
    },
    setFromFilter(state, action: PayloadAction<Dayjs | null | undefined>) {
      state.fromFilter = action.payload;
    },
    setToFilter(state, action: PayloadAction<Dayjs | null | undefined>) {
      state.toFilter = action.payload;
    },
    setSelectedLicenseTypeIdsTemporal(state, action: PayloadAction<number[]>) {
      state.selectedLicenseTypeIdsTemporal = action.payload;
    },
    setShowNotPrinted: (state, action: PayloadAction<boolean>) => {
      state.showNotPrinted = action.payload;
    },
    setNotFormalVerified: (state, action: PayloadAction<boolean>) => {
      state.notFormalVerified = action.payload;
    },
    setSelectedStatuses: (state, action: PayloadAction<number[]>) => {
      state.selectedStatuses = action.payload;
    },
    setSelectedStatusesTemporal: (state, action: PayloadAction<number[]>) => {
      state.selectedStatusesTemporal = action.payload;
    },
    setSelectedSalesPointIds: (state, action: PayloadAction<string[]>) => {
      state.selectedSalesPointIds = action.payload;
    },
    setSelectedSalesPointIdsTemp: (state, action: PayloadAction<string[]>) => {
      state.selectedSalesPointIdsTemp = action.payload;
    },
    setMobileUsed: (state, action: PayloadAction<boolean | null>) => {
      state.mobileUsed = action.payload;
    },
    setMobileUsedTemp: (state, action: PayloadAction<boolean | null>) => {
      state.mobileUsedTemp = action.payload;
    },
    resetFilters(state) {
      state.searchText = initialState.searchText;
      state.selectedLicenseTypeIds = initialState.selectedLicenseTypeIds;
      state.createdAtFromFilter = initialState.createdAtFromFilter;
      state.createdAtToFilter = initialState.createdAtToFilter;
      state.fromFilter = initialState.fromFilter;
      state.toFilter = initialState.toFilter;
      state.selectedLicenseTypeIdsTemporal = initialState.selectedLicenseTypeIdsTemporal;
      state.showNotPrinted = initialState.showNotPrinted;
      state.notFormalVerified = initialState.notFormalVerified;
      state.selectedStatuses = initialState.selectedStatuses;
      state.selectedStatusesTemporal = initialState.selectedStatusesTemporal;
      state.selectedSalesPointIds = initialState.selectedSalesPointIds;
      state.selectedSalesPointIdsTemp = initialState.selectedSalesPointIdsTemp;
      state.mobileUsed = initialState.mobileUsed;
      state.mobileUsedTemp = initialState.mobileUsedTemp;
    },
  },
});

export const huntingLicensesFiltersActions = huntingLicensesFiltersSlice.actions;

export const huntingLicensesFilters = huntingLicensesFiltersSlice.reducer;

export const areHuntingLicensesFiltersAppliedSelector = (state: {
  huntingLicensesFilters: HuntingLicensesFilterState;
}) => {
  const { searchText, selectedLicenseTypeIds, createdAtFromFilter, createdAtToFilter, fromFilter, toFilter } =
    state.huntingLicensesFilters;

  return (
    searchText.length > 0 ||
    selectedLicenseTypeIds.length > 0 ||
    createdAtFromFilter ||
    createdAtToFilter ||
    fromFilter ||
    toFilter
  );
};

export const huntingLicensesSelectedStatusesSelector = (state: {
  huntingLicensesFilters: HuntingLicensesFilterState;
}) => state.huntingLicensesFilters.selectedStatuses;

export const huntingLicensesSelectedStatusesTemporalSelector = (state: {
  huntingLicensesFilters: HuntingLicensesFilterState;
}) => state.huntingLicensesFilters.selectedStatusesTemporal;

export const huntingLicensesSelectedSalesPointIdsSelector = (state: {
  huntingLicensesFilters: HuntingLicensesFilterState;
}) => state.huntingLicensesFilters.selectedSalesPointIds;

export const huntingLicensesSelectedSalesPointIdsTempSelector = (state: {
  huntingLicensesFilters: HuntingLicensesFilterState;
}) => state.huntingLicensesFilters.selectedSalesPointIdsTemp;

export const huntingLicensesMobileUsedSelector = (state: { huntingLicensesFilters: HuntingLicensesFilterState }) =>
  state.huntingLicensesFilters.mobileUsed;

export const huntingLicensesMobileUsedTempSelector = (state: { huntingLicensesFilters: HuntingLicensesFilterState }) =>
  state.huntingLicensesFilters.mobileUsedTemp;

export const huntingLicensesActiveFiltersCountSelector = (state: {
  huntingLicensesFilters: HuntingLicensesFilterState;
}): number => {
  const s = state.huntingLicensesFilters;
  let count = 0;
  if (s.searchText.length > 0) count++;
  if (s.selectedLicenseTypeIdsTemporal.length > 0) count++;
  if (s.selectedStatusesTemporal.length > 0) count++;
  if (s.fromFilter != null) count++;
  if (s.toFilter != null) count++;
  if (s.createdAtFromFilter != null) count++;
  if (s.createdAtToFilter != null) count++;
  if (s.selectedSalesPointIdsTemp.length > 0) count++;
  if (s.mobileUsedTemp !== null) count++;
  if (s.showNotPrinted) count++;
  if (s.notFormalVerified) count++;
  return count;
};
